import React from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Table from '../components/Table/Table';
import useViewVisited from '../features/app/hooks/useViewVisted';
import usePpsis from '../features/PpsiContainer/hooks/usePpsis';
import { EnrichmentData, States } from '../features/PpsiContainer/constants';
import { ppsiPublishedRoute } from '../routes';
import ProgressFullscreen from '../components/common/ProgressFullscreen';
import DataTypes from '../components/Table/dataTypes';
import View from './views';
import PaperContainer from '../components/common/PaperContainer';
import useAlert from '../features/alertSystem/hooks/useAlert';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import { deletePpsi } from 'features/PpsiContainer/actions';
import { getConfigs } from '../features/app/selectors';
import useDeletePpsiNir from '../features/PpsiContainer/hooks/useDeletePpsiNir';
import { FranchiseType } from '../constants/franchiseType';

function getEnrichmentData(rowData) {
  const enrichmentDataName = EnrichmentData.PUBLISHED;
  return rowData[enrichmentDataName];
}

const formatFormulaCode = (formulaCode, version) => {
  return `${formulaCode.substring(0, 3)} ${formulaCode.substring(
    3,
    7
  )} ${formulaCode.substring(7, formulaCode.length)}\\${version}`;
};

const UnpublishColumns = {
  IfoodId: {
    name: 'ifoodID',
    label: 'IFood Id',
    calculateCellValue: rowData => {
      return formatFormulaCode(rowData.formulaCode, rowData.formulaVersion);
    },
    width: 175,
    grow: 0,
    shrink: 0
  },
  PresentationName: {
    name: 'presentationName',
    label: 'Product Name',
    calculateCellValue: (rowData, { language }) => {
      const { info } = getEnrichmentData(rowData);
      return info[language].presentationName;
    }
  },
  LifeCycleStatus: {
    name: 'lifecycleStatus',
    // dataField: 'lifecycleStatus',
    calculateCellValue: rowData => {
      return (
        rowData.publishedPpsiEnrichmentData.referencedIFoodData
          .lifecycleStatus || ''
      );
    },
    // dataType: DataTypes.Date,
    label: 'Lifecycle Status',
    // searchable: false,
    width: 280
    // grow: 0
  },
  ActualUnpublishedDate: {
    name: 'actualUnpublishedDate',
    dataField: 'actualUnpublishedDate',
    dataType: DataTypes.Date,
    label: 'Unpublished Date',
    calculateCellValue: rowData => {
      return getEnrichmentData(rowData).actualUnPublishedDate;
    },
    width: 150,
    grow: 0,
    shrink: 0,
    path: 'root'
  },
  UnpublishedBy: {
    name: 'unpublishedBy',
    label: 'Unpublished By',
    calculateCellValue: rowData => {
      return getEnrichmentData(rowData).unPublishBy;
    },
    width: 250,
    grow: 0
  }
};

const UnpublishedDishListView = () => {
  const franchiseType = useSelector(getConfigs).toJS().franchise;
  const [onDeletePpsiNir] = useDeletePpsiNir();
  const history = useHistory();
  const [isFetchingPpsis, ppsis] = usePpsis({ status: States.UNPUBLISHED });
  const isFetching = isFetchingPpsis;
  const dispatch = useDispatch();
  const { isViewVisited } = useViewVisited(
    View.UnpublishedPpsis.name,
    isFetching
  );
  const isFetched = isViewVisited ? true : !isFetching;
  const [openAlert, closeAlert] = useAlert();

  if (isFetching) {
    return <ProgressFullscreen />;
  }

  const openInstruction = ({ id }) => {
    history.push(ppsiPublishedRoute(id));
  };

  function deleteActionHandler(data, callback) {
    if (data.length > 0) {
      const { getPayloadIds, getPayloadFormulaCodes } = data.reduce(
        (acc, i) => {
          acc.getPayloadIds.push(i.id);
          acc.getPayloadFormulaCodes.push(i.data.formulaCode);
          return acc;
        },
        { getPayloadIds: [], getPayloadFormulaCodes: [] }
      );

      openAlert({
        title: <FormattedMessage id="notify.UnpublishedDeleteActionLabel" />,
        content: <FormattedMessage id="notify.UnpublishedDeleteHelperLabel" />,
        actions: {
          right: [
            <Button
              key="saveAndLeave"
              onClick={() => {
                closeAlert();
              }}
              color="primary"
            >
              <FormattedMessage id="notify.PpsiMultipleLanguageAlertCancel" />
            </Button>,
            <Button
              key="stay"
              onClick={() => {
                closeAlert();
                callback();
                if (franchiseType !== FranchiseType.INGKA) {
                  onDeletePpsiNir(getPayloadIds, getPayloadFormulaCodes);
                } else {
                  dispatch(deletePpsi(getPayloadIds));
                }
              }}
              color="primary"
            >
              <FormattedMessage id="notify.UnpublishedDeleteSuccessLabel" />
            </Button>
          ]
        }
      });
    }
  }

  const deleteHandler = [{ label: 'Delete', onClick: deleteActionHandler }];

  View.UnpublishedPpsis.visit();

  return (
    <PaperContainer showProgress={!isFetched}>
      <Table
        title="unpublishList.unpublishPpsisTableHeader"
        data={ppsis}
        columns={[
          UnpublishColumns.IfoodId,
          UnpublishColumns.PresentationName,
          UnpublishColumns.LifeCycleStatus,
          UnpublishColumns.ActualUnpublishedDate,
          UnpublishColumns.UnpublishedBy
        ]}
        getObjectId={object => object.id}
        defaultSorting={[{ columnName: 'code', direction: 'asc' }]}
        selectable
        onRowClick={openInstruction}
        actions={deleteHandler}
      />
    </PaperContainer>
  );
};

export default UnpublishedDishListView;
